import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useController } from 'react-hook-form';
import { useStore } from '../../../components/store/Store';
import SettingsInputContainer from '../../../components/SettingsInputContainer';
import Container from '../../../components/Container';
import SettingsStatusAlert from './SettingsStatusAlert';
import { Site } from '../../../declarations/models/Site';

export const WebpageConsentSettings: FC = () => {
  const { t } = useTranslation('components');
  const { state } = useStore();
  const isSuperAdmin = state.currentUser?.user?.is_super_admin;

  const {
    field: { value: activateKitCMP, onChange: onChangeActivateKitCMP },
  } = useController<Site, `content.settings.KitCMPuse`>({
    name: `content.settings.KitCMPuse`,
  });

  const {
    field: { value: activateKitCMPbutton, onChange: onChangeActivateKitCMPbutton },
  } = useController<Site, `content.settings.KitCMPbutton`>({
    name: `content.settings.KitCMPbutton`,
  });

  const {
    field: { value: gdprScripts, onChange: onChangeGdprScripts },
  } = useController<Site, `content.settings.gdprScripts`>({
    name: `content.settings.gdprScripts`,
  });

  const {
    field: { value: kitScripts, onChange: onChangeKitScripts },
  } = useController<Site, `content.settings.KitCMPTestScript`>({
    name: `content.settings.KitCMPTestScript`,
  });
  const {
    field: { value: enableGdprInTestEnvironments, onChange: onChangeEnableGdprInTestEnvironments },
  } = useController<Site, `content.settings.enableGdprInTestEnvironments`>({
    name: `content.settings.enableGdprInTestEnvironments`,
  });

  return (
    <>
      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageConsentSettings.KitCMP`)}>
        <Typography>{t(`Settings.WebpageSettings.WebpageConsentSettings.KitCMPdescription`)}</Typography>

        <SettingsStatusAlert
          active={Boolean(activateKitCMP)}
          activeText={t(`Settings.WebpageSettings.WebpageConsentSettings.KitCMPisActive`)}
          inactiveText={t(`Settings.WebpageSettings.WebpageConsentSettings.KitCMPisInactive`)}
        />

        {isSuperAdmin && (
          <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
            <Container fullWidth spaceBetween gap={2} column>
              <Container column left fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableGdprInTestEnvironments}
                      onChange={(e) => onChangeEnableGdprInTestEnvironments(e.target.checked)}
                    />
                  }
                  label={t(`Settings.WebpageSettings.WebpageConsentSettings.enableGdprInTestEnvironments`)}
                />
              </Container>
              <Container column left fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox checked={activateKitCMP} onChange={(e) => onChangeActivateKitCMP(e.target.checked)} />
                  }
                  label={t(`Settings.WebpageSettings.WebpageConsentSettings.activateKitCMP`)}
                />
              </Container>

              {activateKitCMP && (
                <>
                  <Container column left fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={activateKitCMPbutton}
                          onChange={(e) => onChangeActivateKitCMPbutton(e.target.checked)}
                        />
                      }
                      label={t(`Settings.WebpageSettings.WebpageConsentSettings.activateKitCMPbutton`)}
                    />
                  </Container>
                  <Container fullWidth spaceBetween gap={2}>
                    <Container column left fullWidth>
                      <Typography>
                        <strong>{t(`Settings.WebpageSettings.WebpageConsentSettings.KitCMPTestScript`)}</strong>
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        maxRows={50}
                        variant='outlined'
                        value={kitScripts}
                        onChange={(e) => onChangeKitScripts(e.target.value)}
                      />
                    </Container>
                  </Container>
                </>
              )}
            </Container>
          </div>
        )}
      </SettingsInputContainer>
      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageConsentSettings.OtherCMP`)}>
        <Typography>{t(`Settings.WebpageSettings.WebpageConsentSettings.OtherCMPDescription`)}</Typography>
        <SettingsStatusAlert
          active={Boolean(gdprScripts && gdprScripts.length > 0)}
          activeText={t(`Settings.WebpageSettings.WebpageConsentSettings.OtherCMPisActive`)}
          inactiveText={t(`Settings.WebpageSettings.WebpageConsentSettings.OtherCMPisInactive`)}
        />
        {isSuperAdmin && (
          <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
            <Container fullWidth spaceBetween gap={2}>
              <Container column left fullWidth>
                <Typography>
                  <strong>{t(`Settings.WebpageSettings.WebpageConsentSettings.OtherCMPscript`)}</strong>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={10}
                  maxRows={50}
                  variant='outlined'
                  value={gdprScripts}
                  onChange={(e) => onChangeGdprScripts(e.target.value)}
                />
              </Container>
            </Container>
          </div>
        )}
      </SettingsInputContainer>
    </>
  );
};

export default WebpageConsentSettings;
